import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, LineChart } from 'lucide-react';
import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import CDMTable from './components/CDMTable';
import DifferentialTable from './components/DifferentialTable';
import BrainstormBoard from './components/BrainstormBoard';
import CaseFlowPanel, { caseAction } from './components/CaseFlowPanel';
import ClassGapsPanel from './components/ClassGapsPanel';
import Timeline from './components/Timeline';
import CasePanel from './components/CasePanel';
import {
  StudentIndividualBanner, FacilitatorIndividualBanner, PersonalGapsPanel,
} from './components/IndividualPhase';
import LibraryScreen from './components/LibraryScreen';
import ArchiveViewScreen from './components/ArchiveViewScreen';
import InviteModal from './components/InviteModal';
import SettingsModal from './components/SettingsModal';
import ChecklistModal from './components/ChecklistModal';
import AnalyticsModal from './components/AnalyticsModal';
import ArchiveModal from './components/ArchiveModal';
import LoadCaseModal from './components/LoadCaseModal';
import CaseManagerScreen from './components/CaseManagerScreen';
import SurveyModal from './components/SurveyModal';
import AdminDashboard, { LockScreen } from './components/AdminDashboard';
import Toasts from './components/Toasts';
import useSession, { sanitizeKey } from './hooks/useSession';
import useSurvey, { runIdOf } from './hooks/useSurvey';
import useArchives from './hooks/useArchives';
import useCaseLibrary from './hooks/useCaseLibrary';
import useConnection from './hooks/useConnection';
import { ensureSignedIn } from './firebase';
import { getIdentityKey } from './utils/identity';
import { isUnlocked, setUnlocked } from './utils/adminAuth';
import { computeRanking, stepIndex, BOARD_TABS, STEP_LABELS } from './utils/deriveSession';
import { findStream } from './utils/caseStreams';
import MOCK_CASES from './data/mockCases';
import exportToPdf from './utils/exportPdf';
import exportToCsv from './utils/exportCsv';
import { toast } from './utils/toast';

const SURVEY_DELAY_MS = 30000;
const LAST_LOGIN_KEY = 'cdmgrid:last';
const ACTIVE_KEY = 'cdmgrid:active';

const readJson = (storage, key) => {
  try { return JSON.parse(storage.getItem(key)) || null; } catch { return null; }
};

export default function App() {
  const [authState, setAuthState] = useState('pending'); // pending | ready | failed
  const [login, setLogin] = useState(null); // { userName, sessionId, spectator }
  const [screen, setScreen] = useState('main'); // 'main' | 'library' | { archive }
  const [currentView, setCurrentView] = useState('cdm');
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // Phone-friendly: collapse the header down to just tabs, leaving the case
  // panel and working area. Remembered per device.
  const [headerCollapsed, setHeaderCollapsed] = useState(
    () => localStorage.getItem('cdmgrid:headerCollapsed') === '1'
  );
  const [modal, setModal] = useState(null); // settings | checklist | analytics | invite | archive | loadcase
  // Presenter mode: bigger type, no author lines or vote controls, the header
  // collapses to the tabs. Remembered per session (see the effect below).
  const [presenter, setPresenter] = useState(false);
  useEffect(() => {
    if (!login?.sessionId) return;
    setPresenter(localStorage.getItem(`cdmgrid:presenter:${login.sessionId}`) === '1');
  }, [login?.sessionId]);
  // Admin dashboard (survey results + question setup), reachable at ?admin=1.
  const [adminOpen, setAdminOpen] = useState(
    () => new URLSearchParams(window.location.search).get('admin') === '1'
  );
  const [adminUnlocked, setAdminUnlocked] = useState(isUnlocked);
  // The post-case survey appears on a delay after the diagnosis is revealed.
  const [surveyDue, setSurveyDue] = useState(false);
  const [prefill, setPrefill] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const last = readJson(localStorage, LAST_LOGIN_KEY);
    return { name: last?.name || '', session: params.get('session') || last?.session || '' };
  });

  useEffect(() => {
    // Sign-in is best-effort: if the Anonymous provider isn't enabled the app
    // still works using a persistent per-device identity key, so this must not
    // block joining or surface an error. Genuine connectivity problems are
    // reported from the live database socket (useConnection) instead.
    ensureSignedIn().then(() => setAuthState('ready'));
  }, []);

  const identityKey = useMemo(
    () => (authState === 'pending' ? null : getIdentityKey()),
    [authState]
  );

  const handleJoin = (name, sessionInput, spectator = false) => {
    const sessionId = sanitizeKey(sessionInput);
    const userName = spectator ? 'Spectator' : name.trim();
    if (!sessionId || !userName) return;
    setLogin({ userName, sessionId, spectator });
    setCurrentView('cdm');
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({ name: userName, session: sessionId, spectator }));
    if (!spectator) {
      localStorage.setItem(LAST_LOGIN_KEY, JSON.stringify({ name: userName, session: sessionId }));
      window.history.replaceState(null, '', `${window.location.pathname}?session=${encodeURIComponent(sessionId)}`);
    }
  };

  const handleLeave = () => {
    sessionStorage.removeItem(ACTIVE_KEY);
    window.history.replaceState(null, '', window.location.pathname);
    setLogin(null);
    setModal(null);
  };

  // Auto-rejoin: refresh mid-session (sessionStorage) or a ?spectate=1 link.
  useEffect(() => {
    if (authState === 'pending' || login) return;
    const active = readJson(sessionStorage, ACTIVE_KEY);
    if (active?.session && active?.name) {
      handleJoin(active.name, active.session, !!active.spectator);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('spectate') === '1' && params.get('session')) {
      handleJoin('Spectator', params.get('session'), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const connected = useConnection();
  const session = useSession(
    login?.sessionId, login?.userName, identityKey,
    !!login && screen === 'main',
    { spectator: !!login?.spectator }
  );
  const archivesActive = !login && screen !== 'main';
  const { archives, loading: archivesLoading, removeArchive, cloneArchive } = useArchives(identityKey, archivesActive);
  // The case library is read whenever a facilitator might pick or edit a case.
  const { cases: customCases, loading: casesLoading, saveCase, deleteCase } =
    useCaseLibrary(identityKey, !!login && session.isFacilitator);

  const { caseFlow } = session;

  // When the facilitator advances the case, everyone's view follows the step.
  const prevStepRef = useRef(null);
  useEffect(() => {
    const step = caseFlow.active ? caseFlow.step : null;
    if (step && step !== prevStepRef.current) setCurrentView(step);
    prevStepRef.current = step;
  }, [caseFlow.active, caseFlow.step]);

  const currentRanking = useMemo(
    () => computeRanking(session.cdmRows, session.settings.probabilityMode),
    [session.cdmRows, session.settings.probabilityMode]
  );

  // ---- Post-case feedback ----
  // The prompt is keyed to the run (the reveal timestamp), so a lecture that
  // runs three variants collects three sets of answers.
  const runId = runIdOf(caseFlow);
  const survey = useSurvey(
    login?.sessionId, identityKey, runId,
    !!login && !login.spectator && !!runId && screen === 'main'
  );

  // SURVEY_DELAY_MS after the reveal, long enough for the diagnosis and its
  // explanation to land, short enough that the room is still together.
  useEffect(() => {
    setSurveyDue(false);
    if (!runId || !login || login.spectator) return undefined;
    const elapsed = Date.now() - Number(runId);
    if (elapsed >= SURVEY_DELAY_MS) {
      setSurveyDue(true);
      return undefined;
    }
    const timer = setTimeout(() => setSurveyDue(true), SURVEY_DELAY_MS - elapsed);
    return () => clearTimeout(timer);
  }, [runId, login]);

  // ---- Admin dashboard (?admin=1, or the link on the login screen) ----
  const closeAdmin = () => {
    setAdminOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('admin');
    window.history.replaceState(null, '', url.pathname + url.search);
  };
  if (adminOpen) {
    return (
      <>
        {adminUnlocked ? (
          <AdminDashboard
            onClose={closeAdmin}
            onLock={() => { setUnlocked(false); setAdminUnlocked(false); }}
          />
        ) : (
          <LockScreen
            onUnlock={() => { setUnlocked(true); setAdminUnlocked(true); }}
            onClose={closeAdmin}
          />
        )}
        <Toasts />
      </>
    );
  }

  // ---- Library / archive screens (reachable from the login screen) ----
  if (!login) {
    if (screen === 'library') {
      return (
        <>
          <LibraryScreen
            archives={archives}
            loading={archivesLoading}
            onView={(archive) => setScreen({ archive })}
            onClone={async (archive, newId) => {
              const id = await cloneArchive(archive, newId, 'template');
              setPrefill((p) => ({ ...p, session: id }));
              setScreen('main');
              return id;
            }}
            onDelete={(archive) => removeArchive(archive.id)}
            onBack={() => setScreen('main')}
          />
          <Toasts />
        </>
      );
    }
    if (screen?.archive) {
      return (
        <>
          <ArchiveViewScreen archive={screen.archive} onBack={() => setScreen('library')} />
          <Toasts />
        </>
      );
    }
    return (
      <>
        <LoginScreen
          onJoin={handleJoin}
          authState={authState}
          connected={connected}
          initialName={prefill.name}
          initialSession={prefill.session}
          rejoin={readJson(localStorage, LAST_LOGIN_KEY)}
          onOpenLibrary={() => setScreen('library')}
          onOpenAdmin={() => setAdminOpen(true)}
        />
        <Toasts />
      </>
    );
  }

  // ---- In-session ----
  const { userName, sessionId, spectator } = login;
  const {
    settings, isFacilitator, revealed, cdmRows, diffRows, onlineUsers, spentVotes,
    stages, snapshots, myGaps, individualStats, historyBoard, physicalBoard, labsBoard,
  } = session;

  const boards = { history: historyBoard, physical: physicalBoard, labs: labsBoard };

  // Top diseases the class proposed, for the running case summary.
  const differentialSummary = diffRows
    .flatMap((r) => r.diseases)
    // Blind voting: the summary must not leak the ranking either.
    .sort((a, b) => (revealed ? (b.votes || 0) - (a.votes || 0) : 0) || (a.createdAt || 0) - (b.createdAt || 0))
    .slice(0, 6)
    .map((d) => d.text)
    .join(' · ');

  const individualActive = settings.phase === 'individual';
  const studentInIndividual = individualActive && !isFacilitator && !spectator;

  // Diagnostic streams: the source definition for the running case, so the
  // facilitator can start the next queued variant without re-picking it.
  const sourceCase = caseFlow.active
    ? [...customCases, ...MOCK_CASES].find((c) => c.id === caseFlow.sourceCaseId) || null
    : null;
  const nextStream = sourceCase && caseFlow.stream?.nextId
    ? findStream(sourceCase, caseFlow.stream.nextId)
    : null;

  // Locked forward, frozen back: students edit only the current, un-revealed step.
  const viewReadOnly = (view) =>
    spectator ||
    (caseFlow.active && !isFacilitator &&
      (!!caseFlow.completed[view] || stepIndex(view) < stepIndex(caseFlow.step)));

  // Remove is one control (the card's × was the match override, which stays)
  // and every removal can be undone from its toast for a few seconds.
  const removeWithUndo = async (rowId, column, itemId, isPrivate) => {
    const restore = await session.deleteItem(rowId, column, itemId, isPrivate);
    toast('Removed.', 'info', restore ? { action: { label: 'Undo', onClick: restore } } : {});
  };

  const tableProps = (view) => ({
    userName,
    identityKey,
    isFacilitator,
    revealed,
    readOnly: viewReadOnly(view),
    addItem: session.addItem,
    editItem: session.editItem,
    deleteItem: removeWithUndo,
    deleteRow: session.deleteRow,
    vote: session.vote,
  });

  const boardProps = (tab) => ({
    tab,
    board: boards[tab],
    columns: BOARD_TABS[tab].columns,
    rowId: BOARD_TABS[tab].rowId,
    userName,
    identityKey,
    isFacilitator,
    revealed,
    readOnly: viewReadOnly(tab),
    caseActive: caseFlow.active,
    findings: caseFlow.findings[tab] || [],
    addItem: session.addItem,
    editItem: session.editItem,
    deleteItem: removeWithUndo,
    vote: session.vote,
    setMatchOverride: session.setMatchOverride,
    onAddFinding: session.addFinding,
    onRemoveFinding: session.removeFinding,
  });

  // The facilitator's single contextual action for this step, rendered by the
  // header beside the tab strip (which doubles as the progress stepper).
  const handleNextStream = nextStream ? () => {
    session.nextStream(sourceCase);
    setCurrentView('history');
    toast('Next variant loaded, everyone restarts at History with a clean board.', 'success');
  } : null;
  const action = caseAction({
    caseFlow,
    isFacilitator,
    spectator,
    hasNextStream: !!nextStream,
    onRevealMatches: session.revealMatches,
    onAdvance: session.advanceCase,
    onRevealDiagnosis: session.revealDiagnosis,
    onNextStream: handleNextStream,
  });

  const handleMerge = async () => {
    const count = await session.mergeIndividual();
    if (count > 0) {
      toast(`Merged ${count} unique entr${count === 1 ? 'y' : 'ies'}, every student's own gaps are saved.`, 'success');
    }
  };

  return (
    <div className="min-h-screen bg-page px-4 sm:px-6 pb-8" data-presenter={presenter ? '1' : '0'}>
      <div className="max-w-[1900px] mx-auto">
        <Header
          currentView={currentView}
          setCurrentView={setCurrentView}
          userName={userName}
          sessionId={sessionId}
          onlineCount={onlineUsers.length}
          connected={connected}
          spectator={spectator}
          isFacilitator={isFacilitator}
          caseFlow={caseFlow}
          budget={settings.voteMode === 'budget' ? { spent: spentVotes, total: settings.voteBudget } : null}
          onExportPdf={() => exportToPdf({ view: currentView === 'differential' ? 'differential' : 'cdm', sessionId, cdmRows, diffRows, caseFlow })}
          onExportCsv={() => exportToCsv({ view: currentView === 'differential' ? 'differential' : 'cdm', sessionId, cdmRows, diffRows, probabilityMode: settings.probabilityMode, caseFlow })}
          onAddRow={() => session.addRow(false)}
          onAddCriticalRow={() => session.addRow(true)}
          onAddDiffRow={session.addDiffRow}
          onInvite={() => setModal('invite')}
          onLeave={handleLeave}
          onOpenSettings={() => setModal('settings')}
          onSnapshot={() => {
            session.takeSnapshot(`Snapshot ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
            toast('Ranking snapshot saved to the timeline.', 'success');
          }}
          onOpenChecklist={() => setModal('checklist')}
          onOpenAnalytics={() => setModal('analytics')}
          onOpenArchive={() => setModal('archive')}
          onOpenLoadCase={() => setModal('loadcase')}
          onOpenCaseLibrary={() => setModal('cases')}
          onResetCase={() => setConfirmReset(true)}
          onEndCase={() => { session.endCase(); toast('Case ended, boards and grid stay as they are.', 'info'); }}
          presenter={presenter}
          onTogglePresenter={() => setPresenter((v) => {
            localStorage.setItem(`cdmgrid:presenter:${sessionId}`, v ? '0' : '1');
            return !v;
          })}
          collapsed={headerCollapsed}
          onToggleCollapse={() => setHeaderCollapsed((v) => {
            localStorage.setItem('cdmgrid:headerCollapsed', v ? '0' : '1');
            return !v;
          })}
          action={action}
          actionQuiet={individualActive}
        >
          {/* One panel: navigation, progress, and the case's own identity. */}
          <CaseFlowPanel
            caseFlow={caseFlow}
            historyBoard={historyBoard}
            physicalBoard={physicalBoard}
            differentialSummary={differentialSummary}
            isFacilitator={isFacilitator}
            spectator={spectator}
            confirmResetOpen={confirmReset}
            onCancelReset={() => setConfirmReset(false)}
            onResetCase={() => {
              session.resetCase();
              setConfirmReset(false);
              setCurrentView('history');
              toast('Case reset, everyone is back at History with a clean board.', 'success');
            }}
          />
        </Header>

        {isFacilitator && individualActive && (
          <FacilitatorIndividualBanner stats={individualStats} onMerge={handleMerge} />
        )}
        {studentInIndividual && (
          <StudentIndividualBanner view={STEP_LABELS[currentView] || null} />
        )}
        {!spectator && <PersonalGapsPanel gaps={myGaps} />}

        {!caseFlow.active && (
          <CasePanel
            stages={stages}
            isFacilitator={isFacilitator}
            readOnly={spectator}
            onAddStage={session.addStage}
            onRevealStage={session.revealStage}
          />
        )}

        {BOARD_TABS[currentView] ? (
          <BrainstormBoard {...boardProps(currentView)} />
        ) : currentView === 'differential' ? (
          <DifferentialTable rows={diffRows} {...tableProps('differential')} />
        ) : (
          <>
            <CDMTable
              rows={cdmRows}
              probabilityMode={settings.probabilityMode}
              setProbability={session.setProbability}
              {...tableProps('cdm')}
            />
            {/* Ranking timeline (debrief artifact), tucked under the CDM grid */}
            <div className="mt-section">
              <button
                onClick={() => setTimelineOpen(!timelineOpen)}
                className="flex items-center gap-1.5 text-meta font-medium text-ink-2 hover:text-accent transition-colors mb-3"
                aria-expanded={timelineOpen}
              >
                <LineChart className="w-3.5 h-3.5" />
                Ranking timeline {snapshots.length > 0 && `(${snapshots.length} snapshot${snapshots.length === 1 ? '' : 's'})`}
                {timelineOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {timelineOpen && (
                <Timeline
                  snapshots={snapshots}
                  currentRanking={currentRanking}
                  probabilityMode={settings.probabilityMode}
                  revealed={revealed}
                />
              )}
            </div>
          </>
        )}
        {/* What the room collectively missed on this step, facilitator only;
            written at Merge & share or Reveal. On CDM it names the answer,
            so it waits for the final reveal. */}
        {isFacilitator && caseFlow.active && (currentView !== 'cdm' || caseFlow.diagnosisRevealedAt) && (
          <ClassGapsPanel gaps={caseFlow.classGaps?.[currentView]} scopeLabel={STEP_LABELS[currentView]} />
        )}
      </div>

      <footer className="mt-section text-center text-ink-2 text-meta pb-4">
        <p>Created by ZH (C2028)</p>
      </footer>

      {modal === 'invite' && <InviteModal sessionId={sessionId} onClose={() => setModal(null)} />}
      {modal === 'settings' && isFacilitator && (
        <SettingsModal settings={settings} setSettings={session.setSettings} onClose={() => setModal(null)} />
      )}
      {modal === 'checklist' && (
        <ChecklistModal
          cdmRows={cdmRows}
          isFacilitator={isFacilitator}
          onAddCritical={session.addCriticalDiagnoses}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'analytics' && isFacilitator && (
        <AnalyticsModal cdmRows={cdmRows} diffRows={diffRows} onlineUsers={onlineUsers} onClose={() => setModal(null)} />
      )}
      {modal === 'archive' && isFacilitator && (
        <ArchiveModal sessionId={sessionId} onArchive={session.archiveSession} onClose={() => setModal(null)} />
      )}
      {modal === 'loadcase' && isFacilitator && (
        <LoadCaseModal
          activeCase={caseFlow.active ? caseFlow.title : null}
          customCases={customCases}
          onLoad={session.loadCase}
          onManage={() => setModal('cases')}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'cases' && isFacilitator && (
        <CaseManagerScreen
          customCases={customCases}
          loading={casesLoading}
          onSaveCase={saveCase}
          onDeleteCase={deleteCase}
          onStartCase={(caseDef, opts) => {
            session.loadCase(caseDef, opts);
            setCurrentView('history');
            toast(`Case loaded: ${caseDef.title}. The class starts with History.`, 'success');
          }}
          onClose={() => setModal(null)}
        />
      )}
      {/* Students only: the facilitator is usually projecting, and a survey on
          the projector 30s after the reveal would both interrupt the debrief
          and anchor the room's answers. They preview it from the dashboard. */}
      {surveyDue && caseFlow.active && !spectator && !isFacilitator
        && !survey.submitted && !survey.skipped && survey.questions.length > 0 && (
        <SurveyModal
          questions={survey.questions}
          caseTitle={caseFlow.title}
          onSkip={survey.skip}
          onSubmit={(answers) => survey.submit(answers, {
            name: userName,
            role: isFacilitator ? 'facilitator' : 'student',
            caseId: caseFlow.caseId || '',
            caseTitle: caseFlow.title || '',
            streamLabel: caseFlow.stream?.label || '',
          })}
        />
      )}
      <Toasts />
    </div>
  );
}
